/**
 * IngredientRow — one food line inside a sub-block of the menu builder.
 *
 * An "ingredient" is really a FAMILY: the ingredient itself plus its
 * `connectedIngredients` (substitutes). The family is collapsed by default —
 * the row shows ONE active option (main or substitute), a teal `n/total`
 * counter, a 🔁 cycle button and an expand toggle that reveals the full
 * `אפשרות 1..n` list (rendered by ConnectedIngredients).
 *
 * ALL nutrition math comes from src/utils/nutritionCalc — nothing is computed
 * here beyond formatting.
 *
 * This file also exports the two tiny display primitives the other menu cards
 * share (`fmtNum`, `MACROS`) so the formatting of a macro chip is identical at
 * every level of the tree.
 */

import { useEffect, useRef } from "react";
import {
  ArrowsClockwise,
  BookmarkSimple,
  CaretDown,
  CaretUp,
  Hamburger,
  Info,
  Trash,
} from "@phosphor-icons/react";

import {
  AMOUNT_UNITS,
  GRAM_UNIT,
  calcIngredientGroupNutrition,
  calcIngredientNutrition,
} from "../../../utils/nutritionCalc";
import "./MenuCards.css";
import ConnectedIngredients from "./ConnectedIngredients";
import HoverTip from "../../ui/HoverTip";
import NmbIconButton from "./NmbIconButton";

// ============================================================================
// Shared display primitives (imported by the other menu cards)
// ============================================================================

/** Round for display without a trailing ".0"; never renders NaN. */
export const fmtNum = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return String(Math.round(num * 10) / 10);
};

/** The four macros, in the RTL display order the coaches expect. */
export const MACROS = [
  { key: "calories", label: "קלוריות", unit: 'קק"ל' },
  { key: "carbohydrates", label: "פחמימות", unit: "גרם" },
  { key: "protein", label: "חלבון", unit: "גרם" },
  { key: "fat", label: "שומן", unit: "גרם" },
];

/** Hebrew tooltip shown on the amber ⓘ until a quantity is typed. */
export const MISSING_AMOUNT_HINT =
  "עליך להכניס כמות עבור הפריט הזה או אחד מהחלופות, או למחוק אותו מהתפריט.";

/**
 * The units this option may be measured in: grams always, plus every serving
 * unit the food itself defines, kept in the canonical AMOUNT_UNITS order.
 */
export const unitsForOption = (option) => {
  const defined = Object.keys(option?.sizeOptions || {});
  return AMOUNT_UNITS.filter(
    (unit) => unit === GRAM_UNIT || defined.includes(unit)
  );
};

// ============================================================================
// Component
// ============================================================================

const IngredientRow = ({ ingredient, blockIndex, subBlockIndex, index, count, ctx }) => {
  const amountRef = useRef(null);

  const ingredientId = ingredient.ingredientId;
  const options = [ingredient, ...(ingredient.connectedIngredients || [])];
  const activeIndex = Math.min(ctx.activeOptions[ingredientId] || 0, options.length - 1);
  const active = options[activeIndex] || ingredient;
  const hasSubstitutes = options.length > 1;
  const isExpanded = !!ctx.expandedOptions[ingredientId];

  const cardKey = `ing:${ingredientId}`;
  const error = ctx.errors[cardKey];
  const isFlashing = ctx.flashKey === cardKey;

  const activeNutrition = calcIngredientNutrition(active);
  const groupNutrition = calcIngredientGroupNutrition(ingredient);
  const missingAmount = !Number(active.amount);
  const isCustomFood = active.foodType === "customFood";

  // A freshly picked food lands with amount 0 — put the caret in the field.
  useEffect(() => {
    if (ctx.focusIngredientId !== ingredientId) return;
    amountRef.current?.focus();
    amountRef.current?.select();
    ctx.onFocusHandled();
  }, [ctx, ingredientId]);

  const patchActive = (patch) =>
    ctx.onOptionChange(blockIndex, subBlockIndex, index, activeIndex, patch);

  return (
    <div
      className={`nmb-ing ${hasSubstitutes ? "has-substitutes" : ""} ${isFlashing ? "flash" : ""} ${error ? "invalid" : ""}`}
      data-nmb-key={cardKey}
    >
      <div className="nmb-ing-main">
        <div className="nmb-ing-identity">
          <span className="nmb-ing-name" title={active.foodName}>
            {active.foodName}
          </span>

          {hasSubstitutes && (
            <HoverTip text="איזו חלופה מוצגת כרגע מתוך כל החלופות של הפריט. הלקוח/ה יכול/ה להחליף ביניהן בעמוד האישי. השורה ״ערכים תזונתיים למנה״ מציגה את החלופה הזו, ובסיכומים נספר הממוצע של כל החלופות">
              <span className="nmb-alt-counter" tabIndex={0}>
                <span className="nmb-alt-counter-num">{activeIndex + 1}</span>
                <span className="nmb-alt-counter-of">מתוך</span>
                <span className="nmb-alt-counter-num">{options.length}</span>
              </span>
            </HoverTip>
          )}

          {isCustomFood && (
            <span className="nmb-badge nmb-badge-neutral">ערכים למנה</span>
          )}

          {missingAmount && (
            <HoverTip text={MISSING_AMOUNT_HINT}>
              <span className="nmb-missing-hint" aria-label={MISSING_AMOUNT_HINT} tabIndex={0}>
                <Info size={11} weight="fill" />
              </span>
            </HoverTip>
          )}
        </div>

        <div className="nmb-ing-inputs">
          <input
            ref={amountRef}
            type="number"
            min="0"
            step="0.5"
            dir="ltr"
            className={`nmb-amount-input ${missingAmount ? "missing" : ""}`}
            value={active.amount ?? 0}
            onChange={(event) => patchActive({ amount: event.target.value })}
            aria-label={`כמות עבור ${active.foodName}`}
          />

          {isCustomFood ? (
            <HoverTip text="מזון מותאם אישית — הערכים הוזנו למנה אחת, והכמות היא מספר המנות">
              <span className="nmb-unit-static" tabIndex={0}>מנות</span>
            </HoverTip>
          ) : (
            <HoverTip text="יחידת המידה. הכמות מומרת לגרמים לפי המזון, והערכים מחושבים מהם">
              <select
                className="nmb-unit-select"
                value={active.selectedAmount || GRAM_UNIT}
                onChange={(event) => patchActive({ selectedAmount: event.target.value })}
                aria-label={`יחידת מידה עבור ${active.foodName}`}
              >
                {unitsForOption(active).map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </HoverTip>
          )}
        </div>

        <div className="nmb-ing-actions">
          {hasSubstitutes && (
            <NmbIconButton
            label="הצגת החלופה הבאה"
            variant="alt"
            onClick={() => ctx.onCycleOption(ingredientId, options.length)}
          >
              <ArrowsClockwise size={13} weight="fill" />
          </NmbIconButton>
          )}
          <NmbIconButton
            label="העלאת המזון למעלה"
            disabled={index === 0}
            onClick={() => ctx.onMoveIngredient(blockIndex, subBlockIndex, index, -1)}
          >
            <CaretUp size={13} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="הורדת המזון למטה"
            disabled={index === count - 1}
            onClick={() => ctx.onMoveIngredient(blockIndex, subBlockIndex, index, 1)}
          >
            <CaretDown size={13} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="הוספת מזון חלופי (הלקוח/ה יוכל/תוכל להחליף ביניהם)"
            variant="alt"
            onClick={() => ctx.onAddSubstitute(blockIndex, subBlockIndex, index)}
          >
            <Hamburger size={13} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="שמירת המזון כתבנית"
            variant="alt"
            onClick={() => ctx.onSaveIngredientTemplate(blockIndex, subBlockIndex, index)}
          >
            <BookmarkSimple size={13} weight="fill" />
          </NmbIconButton>
          <NmbIconButton
            label="מחיקת המזון"
            variant="danger"
            onClick={() => ctx.onDeleteIngredient(blockIndex, subBlockIndex, index)}
          >
            <Trash size={13} weight="fill" />
          </NmbIconButton>
        </div>
      </div>

      {/* Tier 1 of the live nutrition: the active option, then the family average */}
      <div className="nmb-ing-values">
        <div className="nmb-values-row">
          <span className="nmb-values-label">ערכים תזונתיים למנה</span>
          {MACROS.map((macro) => (
            <span className="nmb-chip" key={macro.key}>
              {macro.label} {fmtNum(activeNutrition[macro.key])}
            </span>
          ))}
        </div>

        {hasSubstitutes && (
          <div className="nmb-values-row average">
            <span className="nmb-values-label">ממוצע כל החלופות</span>
            {MACROS.map((macro) => (
              <span className="nmb-chip mint" key={macro.key}>
                {macro.label} {fmtNum(groupNutrition[macro.key])}
              </span>
            ))}
          </div>
        )}
      </div>

      {hasSubstitutes && (
        <button
          type="button"
          className="nmb-expand-btn"
          aria-expanded={isExpanded}
          onClick={() => ctx.onToggleOptions(ingredientId)}
        >
          <CaretDown
            size={12}
            weight="bold"
            className={`nmb-caret ${isExpanded ? "open" : ""}`}
          />
          <span>
            {isExpanded ? "הסתרת החלופות" : `הצגת כל החלופות (${options.length})`}
          </span>
        </button>
      )}

      {hasSubstitutes && (
        <ConnectedIngredients
          options={options}
          activeIndex={activeIndex}
          isExpanded={isExpanded}
          errors={ctx.errors}
          onSetActive={(optionIndex) => ctx.onSetActiveOption(ingredientId, optionIndex)}
          onOptionChange={(optionIndex, patch) =>
            ctx.onOptionChange(blockIndex, subBlockIndex, index, optionIndex, patch)
          }
          onDeleteOption={(optionIndex) =>
            ctx.onDeleteOption(blockIndex, subBlockIndex, index, optionIndex)
          }
        />
      )}

      {error && <p className="nmb-card-error">{error}</p>}
    </div>
  );
};

export default IngredientRow;
